import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_DOWN_COMMAND,
} from 'lexical';

const DEFAULT_POSITION = { top: 72, left: 24 };

const isPrintableKey = (event) => (
  event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey
);

const canOpenSlashMenu = (editor) => {
  let allowed = false;
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
      return;
    }
    const anchorNode = selection.anchor.getNode();
    if (!$isTextNode(anchorNode)) {
      return;
    }
    if (anchorNode.getType?.() === 'root') {
      return;
    }
    let topLevel = null;
    try {
      topLevel = anchorNode.getTopLevelElementOrThrow();
    } catch (error) {
      return;
    }
    const topLevelType = topLevel.getType?.();
    if (!['paragraph', 'heading', 'quote', 'listitem'].includes(topLevelType)) {
      return;
    }
    const offset = Math.max(0, Math.min(selection.anchor.offset, anchorNode.getTextContentSize()));
    const before = anchorNode.getTextContent().slice(0, offset);
    allowed = before.length === 0 || /\s$/.test(before);
  });
  return allowed;
};

const getCaretPosition = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_POSITION;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return DEFAULT_POSITION;
  }
  const range = selection.getRangeAt(0).cloneRange();
  const rect = range.getBoundingClientRect();
  if (!rect) {
    return DEFAULT_POSITION;
  }
  return {
    top: rect.bottom + 8,
    left: Math.max(12, rect.left),
  };
};

export function SlashCommandPlugin({ items, enabled, labels }) {
  const [editor] = useLexicalComposerContext();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const menuRef = useRef(null);

  const filteredItems = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return items;
    }
    return items.filter((item) => {
      const haystack = [item.id, item.label, item.searchText].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(normalized);
    });
  }, [items, query]);

  useEffect(() => {
    if (selectedIndex >= filteredItems.length) {
      setSelectedIndex(0);
    }
  }, [filteredItems.length, selectedIndex]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onPointerDown = (event) => {
      if (!menuRef.current) {
        return;
      }
      if (!menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event) => {
        if (!enabled) {
          return false;
        }

        if (!isOpen) {
          if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) {
            if (!canOpenSlashMenu(editor)) {
              return false;
            }
            event.preventDefault();
            setPosition(getCaretPosition());
            setQuery('');
            setSelectedIndex(0);
            setIsOpen(true);
            return true;
          }
          return false;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          setIsOpen(false);
          setQuery('');
          return true;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex((current) => (
            filteredItems.length > 0 ? (current + 1) % filteredItems.length : 0
          ));
          return true;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex((current) => (
            filteredItems.length > 0 ? (current - 1 + filteredItems.length) % filteredItems.length : 0
          ));
          return true;
        }

        if (event.key === 'Backspace') {
          event.preventDefault();
          setQuery((current) => {
            if (!current) {
              setIsOpen(false);
              return '';
            }
            return current.slice(0, -1);
          });
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          const selected = filteredItems[selectedIndex] || filteredItems[0];
          if (selected) {
            selected.onSelect();
          }
          setIsOpen(false);
          setQuery('');
          return true;
        }

        if (isPrintableKey(event)) {
          event.preventDefault();
          setQuery((current) => `${current}${event.key.toLowerCase()}`);
          setSelectedIndex(0);
          return true;
        }

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, enabled, filteredItems, isOpen, selectedIndex]);

  if (!enabled || !isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className="report-draft-editor__slash-menu"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
      role="menu"
      aria-label={labels.menuAriaLabel}
    >
      <div className="report-draft-editor__slash-menu-head">/{query || labels.prompt}</div>
      <div className="report-draft-editor__slash-menu-list">
        {filteredItems.length === 0 ? (
          <div className="report-draft-editor__slash-menu-empty">{labels.noCommands}</div>
        ) : filteredItems.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={`report-draft-editor__slash-menu-item${index === selectedIndex ? ' is-active' : ''}`}
            onMouseDown={(event) => {
              event.preventDefault();
              item.onSelect();
              setIsOpen(false);
              setQuery('');
            }}
            role="menuitem"
          >
            <span>{item.label}</span>
            {item.shortcut ? <span className="report-draft-editor__slash-menu-shortcut">{item.shortcut}</span> : null}
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}

SlashCommandPlugin.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    searchText: PropTypes.string,
    shortcut: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
  })).isRequired,
  enabled: PropTypes.bool,
  labels: PropTypes.shape({
    menuAriaLabel: PropTypes.string.isRequired,
    prompt: PropTypes.string.isRequired,
    noCommands: PropTypes.string.isRequired,
  }),
};

SlashCommandPlugin.defaultProps = {
  enabled: true,
  labels: {
    menuAriaLabel: 'Slash commands',
    prompt: '',
    noCommands: 'No commands',
  },
};
